/** A calendar day in the user's time zone, 'YYYY-MM-DD'. */
export type LocalDate = string;

export const DEFAULT_TZ = 'Asia/Manila';

const LOCAL_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ZONE_SUFFIX_RE = /(Z|[+-]\d{2}:\d{2})$/i;
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    // Throws RangeError for an unknown time zone.
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    formatters.set(tz, f);
  }
  return f;
}

function pad(n: number, width: number): string {
  return String(n).padStart(width, '0');
}

/** UTC midnight for a calendar day. setUTCFullYear avoids Date.UTC mapping years 0-99 to 1900s. */
function utcDate(year: number, month1to12: number, day: number): Date {
  const d = new Date(0);
  d.setUTCFullYear(year, month1to12 - 1, day);
  return d;
}

function toLocalDate(d: Date): LocalDate {
  return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1, 2)}-${pad(d.getUTCDate(), 2)}`;
}

function parse(d: LocalDate): { year: number; month: number; day: number } {
  const m = LOCAL_DATE_RE.exec(d);
  if (!m) throw new RangeError(`Expected a 'YYYY-MM-DD' date, got '${d}'`);
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    throw new RangeError(`'${d}' is not a real calendar date`);
  }
  return { year, month, day };
}

/** The calendar day an instant falls on in a time zone (default Asia/Manila). */
export function localDate(at: Date | string, tz: string = DEFAULT_TZ): LocalDate {
  // A zone-less string parses in the machine's zone, so a phone and the Worker would disagree.
  if (typeof at === 'string' && !ZONE_SUFFIX_RE.test(at)) {
    throw new RangeError(`Instant must end with 'Z' or a +/-HH:MM offset (no zone), got '${at}'`);
  }
  const instant = typeof at === 'string' ? new Date(at) : at;
  if (Number.isNaN(instant.getTime())) throw new RangeError(`Invalid instant: ${String(at)}`);
  const parts = formatterFor(tz).formatToParts(instant);
  const get = (type: 'year' | 'month' | 'day'): string => {
    const part = parts.find((p) => p.type === type);
    if (!part) throw new RangeError(`Intl returned no ${type} part`);
    return part.value;
  };
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function daysInMonth(year: number, month1to12: number): number {
  if (!Number.isInteger(month1to12) || month1to12 < 1 || month1to12 > 12) {
    throw new RangeError(`Month must be 1-12, got ${month1to12}`);
  }
  // Day 0 of the next month is the last day of this month.
  return utcDate(year, month1to12 + 1, 0).getUTCDate();
}

export function addDays(d: LocalDate, n: number): LocalDate {
  const { year, month, day } = parse(d);
  return toLocalDate(utcDate(year, month, day + n));
}

/** Adds calendar months, clamping to the month end: 2026-01-31 + 1 => 2026-02-28. */
export function addMonths(d: LocalDate, n: number): LocalDate {
  const { year, month, day } = parse(d);
  const total = year * 12 + (month - 1) + n;
  const newYear = Math.floor(total / 12);
  const newMonth = total - newYear * 12 + 1;
  const newDay = Math.min(day, daysInMonth(newYear, newMonth));
  return `${pad(newYear, 4)}-${pad(newMonth, 2)}-${pad(newDay, 2)}`;
}

/** 0 = Sunday ... 6 = Saturday. */
export function weekday(d: LocalDate): 0 | 1 | 2 | 3 | 4 | 5 | 6 {
  const { year, month, day } = parse(d);
  return utcDate(year, month, day).getUTCDay() as 0 | 1 | 2 | 3 | 4 | 5 | 6;
}

/** Negative if a is before b, 0 if the same day, positive if after. Works with Array.sort. */
export function compareDates(a: LocalDate, b: LocalDate): number {
  parse(a);
  parse(b);
  return a < b ? -1 : a > b ? 1 : 0;
}
